// ============================================================================
// Resource Icons Component
// Shows shared resource availability in the sidebar with real-time updates
// ============================================================================

import { auth } from 'app/auth/auth.js';
import { globalSubscriptionManager } from 'app/state/subscription-manager.js';
import { profileCacheService } from 'shared/services/profile-cache-service.js';
import { getSidebarConfig } from 'shared/data/profile-api.js';
import { getSharedResources, getResourceClaims, claimResource, releaseResource } from './resource-api.js';
import { toastManager } from 'shared/toast/toast.js';
import { escapeHtml, renderIcon } from 'shared/utils/dom-helpers.js';
import { showToast } from 'shared/toast/toast.js';
import { toLayoutPx, rectToLayout } from 'shared/utils/zoom.js';

// ============================================================================
// State
// ============================================================================

let resources = [];                          // shared_resources rows
let claims = new Map();                      // Map<resourceId, { claimed_by, claimed_at, profile }>
let hiddenResources = new Set();             // User's hidden resource IDs
let containerEl = null;
let popoverEl = null;
let activePopoverResourceId = null;
let unsubscribeClaims = null;
let closePopoverHandler = null;
let escapeHandler = null;

const MAX_VISIBLE = 6;

// ============================================================================
// Public API
// ============================================================================

export const resourceIcons = {
    /**
     * Initialize: fetch data, subscribe to realtime, render
     * @param {HTMLElement} container - DOM element to render into
     */
    async init(container) {
        containerEl = container;
        if (!containerEl) return;

        // Load user preferences
        const config = getSidebarConfig();
        hiddenResources = new Set(config.hiddenResources || []);

        // Fetch data in parallel
        const [resResult, claimsResult] = await Promise.all([
            getSharedResources(),
            getResourceClaims()
        ]);

        if (resResult.success) resources = resResult.data || [];
        if (claimsResult.success) {
            claims.clear();
            for (const c of (claimsResult.data || [])) {
                claims.set(c.resource_id, {
                    claimed_by: c.claimed_by,
                    claimed_at: c.claimed_at,
                    profile: c.profiles
                });
            }
        }

        // Subscribe to realtime changes
        unsubscribeClaims = globalSubscriptionManager.subscribe(
            'resource_claims',
            null,
            (payload) => handleRealtimeChange(payload)
        );

        render();
    },

    /**
     * Force re-render (called after preference changes)
     */
    refresh() {
        const config = getSidebarConfig();
        hiddenResources = new Set(config.hiddenResources || []);
        render();
    },

    /**
     * Full cleanup
     */
    cleanup() {
        if (unsubscribeClaims) {
            unsubscribeClaims();
            unsubscribeClaims = null;
        }
        hidePopover();
        resources = [];
        claims.clear();
        hiddenResources.clear();
        containerEl = null;
    }
};

// ============================================================================
// Rendering
// ============================================================================

function render() {
    if (!containerEl) return;

    const visible = resources
        .filter(r => !hiddenResources.has(r.id))
        .slice(0, MAX_VISIBLE);

    if (visible.length === 0) {
        containerEl.innerHTML = '';
        return;
    }

    containerEl.innerHTML = visible.map(r => renderResourceButton(r)).join('');

    // Attach click handlers
    containerEl.querySelectorAll('.resource-icons__btn').forEach(btn => {
        btn.addEventListener('click', (e) => {
            e.stopPropagation();
            const resourceId = btn.dataset.resourceId;
            if (activePopoverResourceId === resourceId) {
                hidePopover();
            } else {
                showPopover(resourceId, btn);
            }
        });
    });
}

function renderResourceButton(resource) {
    const claim = claims.get(resource.id);
    const currentUserId = auth.user?.id;

    let stateClass, title;
    if (!claim) {
        stateClass = 'resource-icons__btn--available';
        title = `${resource.name} - Available`;
    } else if (claim.claimed_by === currentUserId) {
        stateClass = 'resource-icons__btn--yours';
        title = `${resource.name} - You're using this`;
    } else {
        stateClass = 'resource-icons__btn--taken';
        const name = claim.profile?.short_name || claim.profile?.full_name || 'Someone';
        title = `${resource.name} - In use by ${name}`;
    }

    const label = escapeHtml(resource.short_label || resource.name.charAt(0));

    return `<button class="resource-icons__btn ${stateClass}" data-resource-id="${resource.id}" title="${escapeHtml(title)}">
        <span class="resource-icons__btn-label">${label}</span>
    </button>`;
}

// ============================================================================
// Popover
// ============================================================================

function showPopover(resourceId, iconEl) {
    hidePopover();
    activePopoverResourceId = resourceId;

    const resource = resources.find(r => r.id === resourceId);
    if (!resource) return;

    const claim = claims.get(resourceId);
    const currentUserId = auth.user?.id;

    // Create popover element
    popoverEl = document.createElement('div');
    popoverEl.className = 'resource-popover resource-popover--visible';

    let content;
    if (!claim) {
        // Available
        content = `
            <div class="resource-popover__header">
                <span class="resource-popover__res-icon">${renderIcon(resource.icon)}</span>
                <span class="resource-popover__res-name">${escapeHtml(resource.name)}</span>
            </div>
            <div class="resource-popover__status resource-popover__status--available">
                <i class="fas fa-check-circle"></i> Available
            </div>
            <div class="resource-popover__actions">
                <button class="resource-popover__btn resource-popover__btn--claim" data-action="claim">
                    <i class="fas fa-plug"></i> Connect
                </button>
            </div>`;
    } else if (claim.claimed_by === currentUserId) {
        // Yours
        const duration = formatDuration(claim.claimed_at);
        content = `
            <div class="resource-popover__header">
                <span class="resource-popover__res-icon">${renderIcon(resource.icon)}</span>
                <span class="resource-popover__res-name">${escapeHtml(resource.name)}</span>
            </div>
            <div class="resource-popover__status resource-popover__status--yours">
                <i class="fas fa-user-circle"></i> You &middot; ${duration}
            </div>
            <div class="resource-popover__actions">
                <button class="resource-popover__btn resource-popover__btn--release" data-action="release">
                    <i class="fas fa-power-off"></i> Disconnect
                </button>
            </div>`;
    } else {
        // Taken by someone else
        const profile = claim.profile || {};
        const name = profile.short_name || profile.full_name || 'Someone';
        const initial = (profile.full_name || 'S').charAt(0).toUpperCase();
        const duration = formatDuration(claim.claimed_at);
        const avatarHtml = profile.avatar_url
            ? `<img src="${escapeHtml(profile.avatar_url)}" alt="" class="resource-popover__avatar-img">`
            : `<span>${initial}</span>`;

        content = `
            <div class="resource-popover__header">
                <span class="resource-popover__res-icon">${renderIcon(resource.icon)}</span>
                <span class="resource-popover__res-name">${escapeHtml(resource.name)}</span>
            </div>
            <div class="resource-popover__user">
                <div class="resource-popover__avatar">${avatarHtml}</div>
                <div class="resource-popover__user-info">
                    <div class="resource-popover__user-name">${escapeHtml(name)}</div>
                    <div class="resource-popover__duration">${duration}</div>
                </div>
            </div>
            <div class="resource-popover__actions">
                <button class="resource-popover__btn resource-popover__btn--kick" data-action="kick">
                    <i class="fas fa-sign-out-alt"></i> Kick
                </button>
                <button class="resource-popover__btn resource-popover__btn--claim" data-action="kick-and-claim">
                    <i class="fas fa-plug"></i> Kick & Connect
                </button>
            </div>`;
    }

    popoverEl.innerHTML = content;
    document.body.appendChild(popoverEl);

    // Position: prefer above the icon, extending right of sidebar
    positionPopover(popoverEl, iconEl);

    // Action handlers
    popoverEl.querySelectorAll('.resource-popover__btn').forEach(btn => {
        btn.addEventListener('click', async (e) => {
            e.stopPropagation();
            const action = btn.dataset.action;
            btn.disabled = true;
            await handlePopoverAction(action, resourceId);
            hidePopover();
        });
    });

    // Close on outside click (deferred to avoid immediate close)
    requestAnimationFrame(() => {
        closePopoverHandler = (e) => {
            if (popoverEl && !popoverEl.contains(e.target) && !iconEl.contains(e.target)) {
                hidePopover();
            }
        };
        escapeHandler = (e) => {
            if (e.key === 'Escape') hidePopover();
        };
        document.addEventListener('click', closePopoverHandler, true);
        document.addEventListener('keydown', escapeHandler);
    });
}

function hidePopover() {
    if (closePopoverHandler) {
        document.removeEventListener('click', closePopoverHandler, true);
        closePopoverHandler = null;
    }
    if (escapeHandler) {
        document.removeEventListener('keydown', escapeHandler);
        escapeHandler = null;
    }
    if (popoverEl?.parentNode) {
        popoverEl.parentNode.removeChild(popoverEl);
    }
    popoverEl = null;
    activePopoverResourceId = null;
}

function positionPopover(popover, trigger) {
    const triggerRect = rectToLayout(trigger.getBoundingClientRect());
    const padding = 8;

    // Temporarily show to measure
    popover.style.visibility = 'hidden';
    const popoverRect = rectToLayout(popover.getBoundingClientRect());

    // Position: above the trigger, right-aligned with trigger's right edge
    let top = triggerRect.top - popoverRect.height - padding;
    let left = triggerRect.left;

    // If goes above viewport, show below
    if (top < padding) {
        top = triggerRect.bottom + padding;
    }

    // If goes off right edge, shift left
    if (left + popoverRect.width > toLayoutPx(window.innerWidth) - padding) {
        left = toLayoutPx(window.innerWidth) - popoverRect.width - padding;
    }

    // If goes off left edge
    if (left < padding) left = padding;

    popover.style.top = `${top}px`;
    popover.style.left = `${left}px`;
    popover.style.visibility = '';
}

// ============================================================================
// Actions
// ============================================================================

async function handlePopoverAction(action, resourceId) {
    const resource = resources.find(r => r.id === resourceId);
    if (!resource) return;
    const currentUserId = auth.user?.id;

    if (action === 'claim') {
        const result = await claimResource(resourceId);
        if (!result.success) {
            showToast(result.error || 'Failed to connect', 'error');
        } else {
            // Optimistic update - don't wait for realtime
            claims.set(resourceId, {
                claimed_by: currentUserId,
                claimed_at: new Date().toISOString(),
                profile: await profileCacheService.getProfile(currentUserId)
            });
            renderSingleIcon(resourceId);
        }
    } else if (action === 'release' || action === 'kick') {
        const prevClaim = claims.get(resourceId);
        const result = await releaseResource(resourceId);
        if (!result.success) {
            showToast(result.error || (action === 'kick' ? 'Failed to kick' : 'Failed to disconnect'), 'error');
        } else {
            claims.delete(resourceId);
            renderSingleIcon(resourceId);

            // Notify locally (realtime will handle other users)
            const res = resources.find(r => r.id === resourceId);
            if (res && prevClaim) {
                const userName = prevClaim.claimed_by === currentUserId
                    ? 'You'
                    : (prevClaim.profile?.short_name || prevClaim.profile?.full_name || 'Someone');
                toastManager.show({
                    variant: 'resource-release',
                    message: `${userName} released ${res.name}`,
                    iconClass: res.icon || 'fa-desktop'
                });
            }
        }
    } else if (action === 'kick-and-claim') {
        const prevClaim = claims.get(resourceId);
        const releaseResult = await releaseResource(resourceId);
        if (!releaseResult.success) {
            showToast(releaseResult.error || 'Failed to kick', 'error');
            return;
        }
        // Optimistic: clear old claim, notify, show available briefly
        claims.delete(resourceId);
        renderSingleIcon(resourceId);

        const res = resources.find(r => r.id === resourceId);
        if (res && prevClaim) {
            const userName = prevClaim.profile?.short_name || prevClaim.profile?.full_name || 'Someone';
            toastManager.show({
                variant: 'resource-release',
                message: `${userName} released ${res.name}`,
                iconClass: res.icon || 'fa-desktop'
            });
        }

        const claimResult = await claimResource(resourceId);
        if (!claimResult.success) {
            showToast(claimResult.error || 'Failed to connect', 'error');
        } else {
            claims.set(resourceId, {
                claimed_by: currentUserId,
                claimed_at: new Date().toISOString(),
                profile: await profileCacheService.getProfile(currentUserId)
            });
            renderSingleIcon(resourceId);
        }
    }
}

// ============================================================================
// Realtime
// ============================================================================

async function handleRealtimeChange(payload) {
    const { eventType, new: newRow, old: oldRow } = payload;
    const currentUserId = auth.user?.id;

    if (eventType === 'INSERT' && newRow) {
        // Someone claimed a resource - fetch their profile
        let profile = await profileCacheService.getProfile(newRow.claimed_by);
        claims.set(newRow.resource_id, {
            claimed_by: newRow.claimed_by,
            claimed_at: newRow.claimed_at,
            profile: profile || null
        });

        // Re-render the affected icon
        renderSingleIcon(newRow.resource_id);

        // Show notification to everyone
        const resource = resources.find(r => r.id === newRow.resource_id);
        if (resource) {
            const userName = newRow.claimed_by === currentUserId
                ? 'You'
                : (profile?.short_name || profile?.full_name || 'Someone');
            toastManager.show({
                variant: 'resource-claim',
                message: `${userName} connected to ${resource.name}`,
                iconClass: resource.icon || 'fa-desktop'
            });
        }
    } else if (eventType === 'DELETE') {
        const resourceId = oldRow?.resource_id;

        if (resourceId) {
            // Fast path: old row populated (REPLICA IDENTITY FULL)
            handleDeleteClaim(resourceId, oldRow?.claimed_by, currentUserId);
        } else {
            // Fallback: realtime didn't include resource_id, re-fetch to reconcile
            await reconcileClaimsAfterDelete(currentUserId);
        }
    }
}

/**
 * Handle a known resource claim deletion
 */
function handleDeleteClaim(resourceId, deletedClaimedBy, currentUserId) {
    const prevClaim = claims.get(resourceId);
    if (!prevClaim) return; // Already removed by optimistic update

    claims.delete(resourceId);
    renderSingleIcon(resourceId);

    const resource = resources.find(r => r.id === resourceId);
    if (resource) {
        const claimedBy = deletedClaimedBy || prevClaim.claimed_by;
        const userName = claimedBy === currentUserId
            ? 'You'
            : (prevClaim.profile?.short_name || prevClaim.profile?.full_name || 'Someone');
        toastManager.show({
            variant: 'resource-release',
            message: `${userName} released ${resource.name}`,
            iconClass: resource.icon || 'fa-desktop'
        });
    }
}

/**
 * Re-fetch claims and diff against local state to find what was removed
 */
async function reconcileClaimsAfterDelete(currentUserId) {
    const result = await getResourceClaims();
    if (!result.success) return;

    const freshClaims = new Set();
    for (const c of (result.data || [])) {
        freshClaims.add(c.resource_id);
    }

    // Find claims that were in our map but no longer exist in DB
    for (const [resourceId, prevClaim] of claims) {
        if (!freshClaims.has(resourceId)) {
            handleDeleteClaim(resourceId, prevClaim.claimed_by, currentUserId);
        }
    }
}

/**
 * Re-render a single icon without rebuilding the whole row
 */
function renderSingleIcon(resourceId) {
    if (!containerEl) return;
    const resource = resources.find(r => r.id === resourceId);
    if (!resource || hiddenResources.has(resource.id)) return;

    const btn = containerEl.querySelector(`[data-resource-id="${resourceId}"]`);
    if (!btn) return;

    // Update classes and title
    const claim = claims.get(resourceId);
    const currentUserId = auth.user?.id;

    btn.classList.remove('resource-icons__btn--available', 'resource-icons__btn--yours', 'resource-icons__btn--taken');

    if (!claim) {
        btn.classList.add('resource-icons__btn--available');
        btn.title = `${resource.name} - Available`;
    } else if (claim.claimed_by === currentUserId) {
        btn.classList.add('resource-icons__btn--yours');
        btn.title = `${resource.name} - You're using this`;
    } else {
        btn.classList.add('resource-icons__btn--taken');
        const name = claim.profile?.short_name || claim.profile?.full_name || 'Someone';
        btn.title = `${resource.name} - In use by ${name}`;
    }

}

// ============================================================================
// Helpers
// ============================================================================

function formatDuration(isoString) {
    const ms = Date.now() - new Date(isoString).getTime();
    const mins = Math.floor(ms / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ${mins % 60}m ago`;
    return `${Math.floor(hours / 24)}d ago`;
}
